"use client";

// The cross-spot session finder: given my next few hours and a travel radius,
// which spot and which window? Fetches one cacheable bundle of nearby spots'
// raw hours, builds each spot's windows with the user's current preferences
// (the same engine the spot's own card uses), and ranks spot-window pairs
// with an honest travel-time discount.

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { prefScoringKey } from "@/lib/session-planner/preferences";
import type { FinderData } from "@/lib/session-planner/api-types";
import {
  buildFinderSpotsForPreferences,
  driveLabel,
  goTimeLabel,
  rankSessions,
  type PlannerFinderWindow,
  type RankedSession,
} from "@/lib/session-planner/finder";
import type { PlannerSport, SessionPreferences } from "@/lib/session-planner/types";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import { conditionsSportHref } from "@/lib/session-planner/account-preferences";
import { conditionsSpotHref } from "@/lib/seo/conditions-links";
import { SessionSupProfileBeta } from "./SessionPreferencesPanel";

const RADII = [20, 40, 60, 90] as const;
type Radius = (typeof RADII)[number];

const HORIZONS = [
  { key: "3h", label: "Next 3 hours", hours: 3 },
  { key: "6h", label: "Next 6 hours", hours: 6 },
  { key: "any", label: "Today + tomorrow", hours: null },
] as const;
type HorizonKey = (typeof HORIZONS)[number]["key"];

export function FinderEmptyMessage({
  radius,
  horizonKey,
  fitEvidenceNote,
}: {
  radius: number;
  horizonKey: HorizonKey;
  fitEvidenceNote?: string | null;
}) {
  return (
    <p className="mt-4 text-[13px] leading-relaxed text-text-muted">
      {fitEvidenceNote
        ? `${fitEvidenceNote}. Confirm the missing personal-fit detail to rank nearby windows.`
        : <>Nothing within {radius} km clears the bar for your settings
          {horizonKey !== "any" ? " in that time" : " over the next two days"}. Widen the
          radius, or check back as the forecast updates.</>}
    </p>
  );
}

export function FinderSafetyWarnings({
  window,
}: {
  window: PlannerFinderWindow;
}) {
  if (window.warnings.length === 0) return null;
  const safetyState = "safetyState" in window ? window.safetyState : "not-provided";
  return (
    <ul
      className="mt-1.5 flex flex-wrap gap-1.5"
      aria-label="Canonical safety warnings"
      data-safety-state={safetyState}
    >
      {window.warnings.map((warning) => (
        <li
          key={warning}
          className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
        >
          {warning}
        </li>
      ))}
    </ul>
  );
}

const LABEL_STYLE: Record<string, string> = {
  best: "bg-emerald-100 text-emerald-800",
  good: "bg-sky-100 text-sky-800",
  possible: "bg-amber-100 text-amber-800",
};

type Props = {
  slug: string;
  sport: PlannerSport;
  prefs: SessionPreferences;
  /** URL sport segment for links to other spots (e.g. "surf", "wind"). */
  sportUrlKey: string;
};

export function SessionFinderSection({ slug, sport, prefs, sportUrlKey }: Props) {
  const [open, setOpen] = useState(false);
  const [radius, setRadius] = useState<Radius>(40);
  const [horizonKey, setHorizonKey] = useState<HorizonKey>("any");
  const [data, setData] = useState<FinderData | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setState("loading");
    });
    fetch(`/api/conditions/${encodeURIComponent(slug)}/session/finder?radius=${radius}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: FinderData | null) => {
        if (cancelled) return;
        if (d && d.spots.length > 0) {
          setData(d);
          setState("ready");
        } else {
          setState("error");
        }
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [open, slug, radius]);

  const finderResult = useMemo((): {
    ranked: RankedSession<PlannerFinderWindow>[];
    fitEvidenceNote: string | null;
  } => {
    if (!data) return { ranked: [], fitEvidenceNote: null };
    const spots = buildFinderSpotsForPreferences(data, sport, prefs);
    const now = new Date();
    const nowHour = now.getHours() + now.getMinutes() / 60;
    const horizon = HORIZONS.find((h) => h.key === horizonKey)?.hours ?? null;
    return {
      ranked: rankSessions(spots, { nowHour, horizonHours: horizon, maxResults: 5 }),
      fitEvidenceNote: spots.find((spot) => spot.fitEvidenceNote)?.fitEvidenceNote ?? null,
    };
  }, [data, prefs, sport, horizonKey]);
  const { ranked, fitEvidenceNote } = finderResult;

  const scoringKey = prefScoringKey(prefs);
  const exactSupProfile = isSupProfileKey(scoringKey) &&
    data?.availableSupProfiles?.includes(scoringKey)
    ? scoringKey
    : undefined;

  return (
    <div className="mt-6 border-t border-border-light pt-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-sm border border-border-light bg-white px-4 text-[13.5px] font-semibold text-navy transition-colors hover:border-gold/40"
      >
        Where is best near here?
        <span aria-hidden className="text-text-muted">{open ? "▴" : "▾"}</span>
      </button>

      {open ? (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.5px] text-text-muted">
              Radius
            </span>
            {RADII.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRadius(r)}
                aria-pressed={radius === r}
                className={`min-h-[44px] rounded-[10px] border px-3 text-[13px] font-semibold transition-colors ${
                  radius === r
                    ? "border-gold bg-gold text-white"
                    : "border-border-light bg-white text-navy hover:border-gold/40"
                }`}
              >
                {r} km
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.5px] text-text-muted">
              When
            </span>
            {HORIZONS.map((h) => (
              <button
                key={h.key}
                type="button"
                onClick={() => setHorizonKey(h.key)}
                aria-pressed={horizonKey === h.key}
                className={`min-h-[44px] rounded-[10px] border px-3 text-[13px] font-semibold transition-colors ${
                  horizonKey === h.key
                    ? "border-gold bg-gold text-white"
                    : "border-border-light bg-white text-navy hover:border-gold/40"
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>

          {state === "loading" ? (
            <div className="mt-4 h-24 animate-pulse rounded-[14px] bg-warm-bg" />
          ) : null}
          {state === "error" ? (
            <p className="mt-4 text-[13px] text-text-muted">
              We could not load nearby spots right now. Try again shortly.
            </p>
          ) : null}

          {state === "ready" && ranked.length === 0 ? (
            <FinderEmptyMessage
              radius={radius}
              horizonKey={horizonKey}
              fitEvidenceNote={fitEvidenceNote}
            />
          ) : null}

          {state === "ready" && ranked.length > 0 ? (
            <ol className="mt-4 space-y-2">
              {ranked.map((r, i) => {
                const isHere = r.slug === slug;
                const inner = (
                  <div
                    className={`rounded-[14px] border px-4 py-3 transition-colors ${
                      i === 0
                        ? "border-gold/40 bg-gold/[0.06]"
                        : "border-border-light bg-white hover:border-gold/30"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-[15px] font-semibold text-navy">
                        {r.window.dayOffset === 1 ? "Tomorrow " : "Go at "}
                        {goTimeLabel(r.effectiveStartHour)}
                      </span>
                      <span className="text-[14px] text-navy">· {r.name}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${LABEL_STYLE[r.window.label] ?? "bg-warm-bg text-text-muted"}`}
                      >
                        {scoreOutOf100(r.window.avgScore)} window
                      </span>
                      {exactSupProfile ? (
                        <SessionSupProfileBeta
                          scoringKey={exactSupProfile}
                          surface="Finder"
                        />
                      ) : null}
                    </div>
                    <p className="mt-1 text-[12.5px] leading-snug text-text-muted">
                      {isHere ? "Right here" : driveLabel(r.driveMinutes)}
                      {" · "}
                      {r.window.timeLabel}
                      {" · "}
                      {r.window.headline}
                    </p>
                    {"evidenceNote" in r.window && r.window.evidenceNote ? (
                      <p className="mt-1 text-[11.5px] font-medium text-amber-800">
                        {r.window.evidenceNote}
                      </p>
                    ) : null}
                    <FinderSafetyWarnings window={r.window} />
                  </div>
                );
                return (
                  <li key={`${r.slug}-${r.window.dayOffset}-${r.window.startHour}`}>
                    {isHere ? (
                      inner
                    ) : (
                      <Link
                        href={exactSupProfile
                          ? conditionsSportHref(r.slug, sport, exactSupProfile)
                          : conditionsSpotHref(r.slug, sportUrlKey)}
                        className="block no-underline"
                      >
                        {inner}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : null}

          {state === "ready" ? (
            <p className="mt-3 text-[11px] leading-relaxed text-text-muted">
              Drive times are estimates from straight-line distance. Windows use each spot&apos;s
              own forecast and your preferences.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
